import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { Boxes, Clock, FileText, Plus, RefreshCcw, X, Zap } from "lucide-react";
import { toast } from "sonner";

import {
  readCloudWalletAccountsPublic,
  type CloudWalletAccountPublic,
  type CloudWalletSummaryResponse,
} from "@/lib/cloud-wallet";
import { apiRequest } from "@/lib/api";
import {
  mergeCommitments,
  normalizeCommitments,
  POOLED_COMPUTE_API,
  readCachedCommitments,
  removeCachedCommitment,
  type PoolCommitment,
  type PooledComputeResponse,
  type PoolStatus,
  writeCachedCommitments,
} from "@/lib/pooled-compute";
import {
  assignHardwareCostBands,
  hardwareCostBandLabel,
  type HardwareCostBand,
} from "@/lib/hardware-cost-band";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/pooled-compute")({
  head: () => ({ meta: [{ title: "Store · GENRL Wallet" }] }),
  component: PooledComputePage,
});

type HardwareOption = {
  id: string;
  label: string;
  className: string;
};

type HardwareOptionWithCostBand = HardwareOption & {
  costBand: HardwareCostBand;
};

type ProviderPool = {
  slug: string;
  name: string;
  status: PoolStatus;
  statusLabel: string;
  committedGpuCount: number;
  availableUsd: number;
  accounts: number;
  hardware: HardwareOption[];
};

type TaskOption = {
  id: string;
  label: string;
};

const TASK_OPTIONS: TaskOption[] = [
  { id: "inference", label: "Inference" },
  { id: "training", label: "Training" },
  { id: "fine_tuning", label: "Fine-tuning" },
  { id: "batch", label: "Batch processing" },
  { id: "notebooks", label: "Notebooks" },
  { id: "rendering", label: "Rendering" },
  { id: "storage", label: "Storage" },
  { id: "data_processing", label: "Data processing" },
];

const PROVIDER_CATALOG: Array<Pick<ProviderPool, "slug" | "name" | "hardware">> = [
  {
    slug: "runpod",
    name: "RunPod",
    hardware: [
      { id: "rtx-3090", label: "NVIDIA RTX 3090", className: "Budget inference" },
      { id: "rtx-4000-ada", label: "NVIDIA RTX 4000 Ada", className: "Efficient inference" },
      { id: "rtx-5000-ada", label: "NVIDIA RTX 5000 Ada", className: "Workstation" },
      { id: "rtx-4090", label: "NVIDIA RTX 4090", className: "Inference" },
      { id: "rtx-a6000", label: "NVIDIA RTX A6000", className: "Training" },
      { id: "l40", label: "NVIDIA L40", className: "Inference" },
      { id: "l40s", label: "NVIDIA L40S", className: "Inference" },
      { id: "a100-80gb", label: "NVIDIA A100 80GB", className: "High memory" },
      { id: "h100-80gb", label: "NVIDIA H100 80GB", className: "Frontier" },
      { id: "h200-141gb", label: "NVIDIA H200 141GB", className: "Frontier memory" },
    ],
  },
  {
    slug: "hyperstack",
    name: "Hyperstack",
    hardware: [
      { id: "a10", label: "NVIDIA A10", className: "Inference" },
      { id: "l4", label: "NVIDIA L4", className: "Efficient inference" },
      { id: "l40", label: "NVIDIA L40", className: "Inference" },
      { id: "l40s", label: "NVIDIA L40S", className: "Inference" },
      { id: "rtx-a6000", label: "NVIDIA RTX A6000", className: "Workstation" },
      { id: "a100-pcie", label: "NVIDIA A100 PCIe", className: "Training" },
      { id: "h100-pcie", label: "NVIDIA H100 PCIe", className: "Frontier" },
      { id: "h100-sxm", label: "NVIDIA H100 SXM", className: "Frontier cluster" },
      { id: "h200-sxm", label: "NVIDIA H200 SXM", className: "Frontier memory" },
    ],
  },
  {
    slug: "lambda_cloud",
    name: "Lambda",
    hardware: [
      { id: "a10", label: "NVIDIA A10", className: "Inference" },
      { id: "rtx-a6000", label: "NVIDIA RTX A6000", className: "Workstation" },
      { id: "l40s", label: "NVIDIA L40S", className: "Inference" },
      { id: "a100", label: "NVIDIA A100", className: "Training" },
      { id: "h100", label: "NVIDIA H100", className: "Frontier" },
      { id: "gb200", label: "NVIDIA GB200", className: "Frontier systems" },
    ],
  },
  {
    slug: "aws",
    name: "AWS",
    hardware: [
      { id: "g5", label: "G5 · NVIDIA A10G", className: "Inference" },
      { id: "g6", label: "G6 · NVIDIA L4", className: "Efficient inference" },
      { id: "g6e", label: "G6e · NVIDIA L40S", className: "Inference" },
      { id: "p4d", label: "P4d · NVIDIA A100", className: "Training" },
      { id: "p4de", label: "P4de · NVIDIA A100 80GB", className: "High memory" },
      { id: "p5", label: "P5 · NVIDIA H100", className: "Frontier" },
      { id: "p5e", label: "P5e · NVIDIA H200", className: "Frontier memory" },
    ],
  },
  {
    slug: "google_cloud",
    name: "Google Cloud",
    hardware: [
      { id: "l4", label: "NVIDIA L4", className: "Inference" },
      { id: "t4", label: "NVIDIA T4", className: "Budget inference" },
      { id: "v100", label: "NVIDIA V100", className: "Legacy training" },
      { id: "a100", label: "NVIDIA A100", className: "Training" },
      { id: "a100-mega", label: "NVIDIA A100 MegaGPU", className: "High memory" },
      { id: "h100", label: "NVIDIA H100", className: "Frontier" },
      { id: "h100-mega", label: "NVIDIA H100 MegaGPU", className: "Frontier cluster" },
    ],
  },
  {
    slug: "vast_ai",
    name: "Vast.ai",
    hardware: [
      { id: "rtx-3080", label: "NVIDIA RTX 3080", className: "Spot" },
      { id: "rtx-3090", label: "NVIDIA RTX 3090", className: "Spot" },
      { id: "rtx-3090-ti", label: "NVIDIA RTX 3090 Ti", className: "Spot" },
      { id: "rtx-4090", label: "NVIDIA RTX 4090", className: "Spot" },
      { id: "rtx-6000-ada", label: "NVIDIA RTX 6000 Ada", className: "Spot workstation" },
      { id: "a6000", label: "NVIDIA RTX A6000", className: "Spot" },
      { id: "a100", label: "NVIDIA A100", className: "Spot training" },
      { id: "h100", label: "NVIDIA H100", className: "Spot frontier" },
    ],
  },
];

function fmtGpuHours(value: number) {
  return `${Number(value || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })} GPU·h`;
}

function fmtGpuCount(value: number) {
  return `${Number(value || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })} GPU${Number(value || 0) === 1 ? "" : "s"}`;
}

function pooledHardwareOptionLabel(
  hardware?: Pick<HardwareOptionWithCostBand, "label" | "costBand"> | null,
) {
  return hardware ? `${hardware.label} · ${hardwareCostBandLabel(hardware.costBand)}` : "—";
}

function taskLabels(tasks: string[] | undefined) {
  const labels = new Map(TASK_OPTIONS.map((task) => [task.id, task.label]));
  const rows = (tasks || []).map((task) => labels.get(task) || task).filter(Boolean);
  return rows.length > 0 ? rows.join(", ") : "Unspecified";
}

function relTime(value?: string) {
  if (!value) return "just now";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "just now";
  const mins = Math.max(0, Math.floor((Date.now() - date.getTime()) / 60000));
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

function snapshotForProvider(summary: CloudWalletSummaryResponse | null, slug: string) {
  const accounts = Array.isArray(summary?.accounts) ? summary.accounts : [];
  return accounts.filter((account) => account.provider_slug === slug);
}

function demandStatusForProvider(
  providerCommitments: PoolCommitment[],
): Pick<ProviderPool, "status" | "statusLabel"> {
  if (providerCommitments.some((item) => item.status === "confirmed")) {
    return { status: "confirmed", statusLabel: "Confirmed" };
  }
  if (providerCommitments.some((item) => item.status === "bidding"))
    return { status: "bidding", statusLabel: "Bidding" };
  if (providerCommitments.some((item) => item.status === "negotiation"))
    return { status: "negotiation", statusLabel: "In negotiation" };
  if (providerCommitments.length > 0) {
    return { status: "queued", statusLabel: "Demand queued" };
  }
  return { status: "open", statusLabel: "Open for demand" };
}

function formatWaitlistTime(commitments: PoolCommitment[]) {
  const waiting = commitments
    .filter((item) => item.status !== "complete")
    .map((item) => new Date(item.createdAt).getTime())
    .filter((value) => Number.isFinite(value));
  if (waiting.length === 0) return "Open";
  const oldest = Math.min(...waiting);
  const mins = Math.max(0, Math.floor((Date.now() - oldest) / 60000));
  if (mins < 1) return "Just joined";
  if (mins < 60) return `${mins}m`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h`;
  return `${Math.floor(hours / 24)}d`;
}

function demandMetrics(commitments: PoolCommitment[]) {
  const hardwareCounts = new Map<string, number>();
  let gpuCount = 0;
  for (const item of commitments) {
    gpuCount += Number(item.gpuCount || 0);
    const label = item.hardwareLabel || "Hardware";
    hardwareCounts.set(label, (hardwareCounts.get(label) || 0) + Number(item.gpuCount || 0));
  }
  const mostCommonHardware =
    [...hardwareCounts.entries()].sort((a, b) => b[1] - a[1])[0]?.[0] || "No demand yet";
  return {
    mostCommonHardware,
    gpuCount,
    waitlistTime: formatWaitlistTime(commitments),
  };
}

function buildPools(
  accounts: CloudWalletAccountPublic[],
  summary: CloudWalletSummaryResponse | null,
  commitments: PoolCommitment[],
): ProviderPool[] {
  return PROVIDER_CATALOG.map((provider) => {
    const providerAccounts = accounts.filter((account) => account.provider_slug === provider.slug);
    const snapshots = snapshotForProvider(summary, provider.slug);
    const providerCommitments = commitments.filter((item) => item.providerSlug === provider.slug);
    const committedGpuCount = providerCommitments.reduce((sum, item) => sum + item.gpuCount, 0);
    const availableUsd = snapshots.reduce((sum, snapshot) => sum + Number(snapshot.credit || 0), 0);
    const status = demandStatusForProvider(providerCommitments);
    return {
      ...provider,
      ...status,
      committedGpuCount,
      availableUsd,
      accounts: providerAccounts.length,
    };
  });
}

export function PooledComputePage() {
  const [accounts, setAccounts] = useState<CloudWalletAccountPublic[]>([]);
  const [summary, setSummary] = useState<CloudWalletSummaryResponse | null>(null);
  const [commitments, setCommitments] = useState<PoolCommitment[]>([]);
  const [selectedSlug, setSelectedSlug] = useState(PROVIDER_CATALOG[0].slug);
  const [hardwareId, setHardwareId] = useState(
    `${PROVIDER_CATALOG[0].slug}:${PROVIDER_CATALOG[0].hardware[0].id}`,
  );
  const [gpuCount, setGpuCount] = useState(1);
  const [durationDays, setDurationDays] = useState(30);
  const [intendedTasks, setIntendedTasks] = useState<string[]>(["inference"]);
  const [currentProviderSlug, setCurrentProviderSlug] = useState("");
  const [currentHardwareId, setCurrentHardwareId] = useState("");
  const [currentGpuCount, setCurrentGpuCount] = useState(0);
  const [currentContractDaysLeft, setCurrentContractDaysLeft] = useState(0);
  const [currentJobTypes, setCurrentJobTypes] = useState<string[]>([]);
  const [commitModalOpen, setCommitModalOpen] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const refresh = async (silent = false) => {
    if (silent) setRefreshing(true);
    else setLoading(true);
    const [nextAccounts, nextSummary, nextCommitments] = await Promise.allSettled([
      readCloudWalletAccountsPublic(),
      apiRequest<CloudWalletSummaryResponse>("/api/wallet/summary", { timeoutMs: 20000 }),
      apiRequest<PooledComputeResponse>(POOLED_COMPUTE_API, { timeoutMs: 20000 }),
    ]);
    setAccounts(nextAccounts.status === "fulfilled" ? nextAccounts.value : []);
    setSummary(nextSummary.status === "fulfilled" ? nextSummary.value : null);
    if (nextCommitments.status === "fulfilled") {
      const nextRows = mergeCommitments(
        normalizeCommitments(nextCommitments.value.commitments),
        readCachedCommitments(),
      );
      setCommitments(nextRows);
      writeCachedCommitments(nextRows);
    } else if (!silent) {
      toast.error(
        nextCommitments.reason instanceof Error
          ? nextCommitments.reason.message
          : "Could not load pooled compute demand.",
      );
      setCommitments((current) => mergeCommitments(current, readCachedCommitments()));
    }
    setLoading(false);
    setRefreshing(false);
  };

  const refreshPoolContext = async () => {
    setRefreshing(true);
    const [nextAccounts, nextSummary] = await Promise.allSettled([
      readCloudWalletAccountsPublic(),
      apiRequest<CloudWalletSummaryResponse>("/api/wallet/summary", { timeoutMs: 20000 }),
    ]);
    setAccounts(nextAccounts.status === "fulfilled" ? nextAccounts.value : []);
    setSummary(nextSummary.status === "fulfilled" ? nextSummary.value : null);
    setRefreshing(false);
  };

  useEffect(() => {
    void refresh();
  }, []);

  const pools = useMemo(
    () => buildPools(accounts, summary, commitments),
    [accounts, summary, commitments],
  );
  const hardwareOptions = useMemo(() => {
    const seen = new Set<string>();
    const uniqueHardwareOptions = PROVIDER_CATALOG.flatMap((provider) =>
      provider.hardware.map((hardware) => ({
        ...hardware,
        id: `${provider.slug}:${hardware.id}`,
        providerSlug: provider.slug,
        providerName: provider.name,
      })),
    ).filter((hardware) => {
      const key = hardware.label.toLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
    const costBands = assignHardwareCostBands(uniqueHardwareOptions, () => undefined);
    return uniqueHardwareOptions.map((hardware, index) => ({
      ...hardware,
      costBand: costBands[index]!,
    }));
  }, []);
  const selectedPool = pools.find((pool) => pool.slug === selectedSlug) || pools[0];
  const selectedHardware =
    hardwareOptions.find((item) => item.id === hardwareId) || hardwareOptions[0];
  const metrics = useMemo(() => demandMetrics(commitments), [commitments]);
  useEffect(() => {
    if (!hardwareOptions.some((item) => item.id === hardwareId)) {
      setHardwareId(hardwareOptions[0]?.id || "");
    }
  }, [hardwareId, hardwareOptions]);

  const openCommitModal = (slug = "open_pool") => {
    setSelectedSlug(slug);
    setHardwareId(hardwareOptions[0]?.id || "");
    setCurrentProviderSlug("");
    setCurrentHardwareId("");
    setCurrentGpuCount(0);
    setCurrentContractDaysLeft(0);
    setCurrentJobTypes([]);
    setIntendedTasks(["inference"]);
    setTermsAccepted(false);
    setCommitModalOpen(true);
  };

  const handleCommit = async () => {
    const targetPool = selectedSlug === "open_pool" ? null : selectedPool;
    const targetProviderSlug = targetPool?.slug || "open_pool";
    const targetProviderName = targetPool?.name || "Open pool";
    if (!selectedHardware) return;
    if (gpuCount <= 0 || durationDays <= 0) {
      toast.error("Add a positive GPU count and duration.");
      return;
    }
    if (intendedTasks.length === 0) {
      toast.error("Choose at least one intended task.");
      return;
    }
    if (!termsAccepted) {
      toast.error("Accept the commitment terms before submitting demand.");
      return;
    }
    try {
      const now = new Date().toISOString();
      const current = {
        providerSlug: currentProviderSlug || "",
        providerName: pools.find((pool) => pool.slug === currentProviderSlug)?.name || "",
        hardwareId: currentHardwareId || "",
        hardwareLabel:
          hardwareOptions.find((hardware) => hardware.id === currentHardwareId)?.label || "",
        gpuCount: currentGpuCount > 0 ? currentGpuCount : 0,
        contractDaysLeft: currentContractDaysLeft > 0 ? currentContractDaysLeft : 0,
        jobTypes: currentJobTypes,
      };
      const submitted: PoolCommitment = {
        id: `local_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`,
        providerSlug: targetProviderSlug,
        providerName: targetProviderName,
        hardwareId: selectedHardware.id,
        hardwareLabel: selectedHardware.label,
        gpuCount,
        durationDays,
        gpuHours: gpuCount * durationDays * 24,
        intendedTasks,
        current:
          current.providerSlug ||
          current.hardwareId ||
          current.gpuCount ||
          current.contractDaysLeft ||
          current.jobTypes.length
            ? current
            : undefined,
        status: "queued",
        termsAccepted: true,
        termsAcceptedAt: now,
        createdAt: now,
      };
      const payload = await apiRequest<PooledComputeResponse>(POOLED_COMPUTE_API, {
        method: "POST",
        body: {
          providerSlug: targetProviderSlug,
          providerName: targetProviderName,
          hardwareId: selectedHardware.id,
          hardwareLabel: selectedHardware.label,
          gpuCount,
          durationDays,
          intendedTasks,
          currentProviderSlug: currentProviderSlug || undefined,
          currentProviderName:
            pools.find((pool) => pool.slug === currentProviderSlug)?.name || undefined,
          currentHardwareId: currentHardwareId || undefined,
          currentHardwareLabel:
            hardwareOptions.find((hardware) => hardware.id === currentHardwareId)?.label ||
            undefined,
          currentGpuCount: currentGpuCount > 0 ? currentGpuCount : undefined,
          currentContractDaysLeft:
            currentContractDaysLeft > 0 ? currentContractDaysLeft : undefined,
          currentJobTypes,
          termsAccepted,
        },
        timeoutMs: 20000,
      });
      const nextRows = mergeCommitments(
        normalizeCommitments(payload.commitments),
        [submitted],
        commitments,
        readCachedCommitments(),
      );
      setCommitments(nextRows);
      writeCachedCommitments(nextRows);
      setCommitModalOpen(false);
      setTermsAccepted(false);
      void refreshPoolContext();
      toast.success(`Committed demand to ${targetProviderName}`);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Could not commit pooled demand.");
    }
  };

  const clearCommitment = async (id: string) => {
    try {
      const payload = await apiRequest<PooledComputeResponse>(POOLED_COMPUTE_API, {
        method: "DELETE",
        body: { id },
        timeoutMs: 20000,
      });
      const cacheAfterDelete = removeCachedCommitment(id);
      const nextRows = mergeCommitments(
        normalizeCommitments(payload.commitments),
        cacheAfterDelete,
      );
      setCommitments(nextRows);
      writeCachedCommitments(nextRows);
      toast.success("Demand commitment removed");
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Could not remove demand commitment.");
    }
  };

  return (
    <main className="wallet-ops-shell flex-1">
      <div className="wallet-ops-container space-y-6">
        <header className="wallet-ops-topbar">
          <div className="wallet-ops-brand">
            <div className="wallet-ops-brand-box">
              <Boxes className="h-4 w-4" />
            </div>
            <div>
              <p className="wallet-ops-label !text-[var(--wallet-ops-fg)]">Store</p>
              <p className="wallet-ops-subtle text-[11px] uppercase tracking-[0.1em]">
                Compute capacity, commitments and provider pools
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              void refresh(true);
            }}
            disabled={refreshing}
            className="wallet-ops-icon-button"
            aria-label="Refresh pooled compute"
            title="Refresh pooled compute"
          >
            <RefreshCcw className={cn("h-4 w-4", refreshing && "animate-spin")} />
          </button>
        </header>

        <section className="wallet-ops-panel mt-6 px-6 py-10 text-center">
          <button type="button" onClick={() => openCommitModal()} className="wallet-ops-button">
            <Plus className="h-3.5 w-3.5" />
            Commit demand
          </button>
          <p className="mt-3 text-[11px] text-[var(--wallet-ops-muted)]">
            Commit demand against an open compute pool. Wallets are not required.
          </p>
        </section>

        <section className="wallet-ops-grid-frame lg:grid-cols-3">
          <DemandMetric
            label="Most common hardware"
            value={metrics.mostCommonHardware}
            hint="Weighted by requested GPUs"
          />
          <DemandMetric
            label="GPUs requested"
            value={fmtGpuCount(metrics.gpuCount)}
            hint={`${commitments.length} active demand ${commitments.length === 1 ? "entry" : "entries"}`}
          />
          <DemandMetric
            label="Current waitlist time"
            value={metrics.waitlistTime}
            hint="Oldest open demand age"
          />
        </section>

        <section className="wallet-ops-grid-frame lg:grid-cols-[minmax(0,1fr)]">
          <div className="wallet-ops-panel-flat">
            <div className="wallet-ops-panel-header">
              <div>
                <p className="wallet-ops-label">Demand ledger</p>
                <p className="mt-1 text-[13px] text-[var(--wallet-ops-subtle)]">
                  Recent commitments for planning and future buying advice.
                </p>
              </div>
              <Clock className="h-4 w-4 text-[var(--wallet-ops-muted)]" />
            </div>
            {commitments.length === 0 ? (
              <div className="px-5 py-10 text-center text-[13px] text-[var(--wallet-ops-muted)]">
                No demand has been committed yet.
              </div>
            ) : (
              <div className="divide-y divide-[var(--wallet-ops-border)]">
                {commitments.slice(0, 8).map((item) => (
                  <div
                    key={item.id}
                    className="grid gap-3 px-5 py-4 md:grid-cols-[minmax(0,1fr)_86px] md:items-center"
                  >
                    <div>
                      <p className="text-[14px] font-medium text-[var(--wallet-ops-fg)]">
                        {item.providerName} · {item.hardwareLabel}
                      </p>
                      <p className="mt-1 text-[11px] uppercase tracking-[0.08em] text-[var(--wallet-ops-muted)]">
                        {fmtGpuCount(item.gpuCount)} · {item.durationDays} days ·{" "}
                        {fmtGpuHours(item.gpuCount * item.durationDays * 24)} ·{" "}
                        {relTime(item.createdAt)}
                      </p>
                      <p className="mt-1 text-[12px] text-[var(--wallet-ops-subtle)]">
                        {taskLabels(item.intendedTasks)}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => void clearCommitment(item.id)}
                      className="wallet-ops-button !min-h-0 justify-center px-2 py-1.5 text-[10px]"
                    >
                      Remove
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>
      </div>
      {commitModalOpen && (
        <CommitDemandModal
          selectedPool={selectedPool}
          selectedHardware={selectedHardware}
          pools={pools}
          hardwareOptions={hardwareOptions}
          selectedSlug={selectedSlug}
          hardwareId={hardwareId}
          gpuCount={gpuCount}
          durationDays={durationDays}
          intendedTasks={intendedTasks}
          currentProviderSlug={currentProviderSlug}
          currentHardwareId={currentHardwareId}
          currentGpuCount={currentGpuCount}
          currentContractDaysLeft={currentContractDaysLeft}
          currentJobTypes={currentJobTypes}
          termsAccepted={termsAccepted}
          onClose={() => {
            setCommitModalOpen(false);
            setTermsAccepted(false);
          }}
          onSelectedSlugChange={(slug) => {
            setSelectedSlug(slug);
            setHardwareId(hardwareOptions[0]?.id || "");
            setTermsAccepted(false);
          }}
          onHardwareIdChange={setHardwareId}
          onGpuCountChange={setGpuCount}
          onDurationDaysChange={setDurationDays}
          onIntendedTasksChange={setIntendedTasks}
          onCurrentProviderSlugChange={setCurrentProviderSlug}
          onCurrentHardwareIdChange={setCurrentHardwareId}
          onCurrentGpuCountChange={setCurrentGpuCount}
          onCurrentContractDaysLeftChange={setCurrentContractDaysLeft}
          onCurrentJobTypesChange={setCurrentJobTypes}
          onTermsAcceptedChange={setTermsAccepted}
          onCommit={() => void handleCommit()}
        />
      )}
    </main>
  );
}

function CommitDemandModal({
  selectedPool,
  selectedHardware,
  pools,
  hardwareOptions,
  selectedSlug,
  hardwareId,
  gpuCount,
  durationDays,
  intendedTasks,
  currentProviderSlug,
  currentHardwareId,
  currentGpuCount,
  currentContractDaysLeft,
  currentJobTypes,
  termsAccepted,
  onClose,
  onSelectedSlugChange,
  onHardwareIdChange,
  onGpuCountChange,
  onDurationDaysChange,
  onIntendedTasksChange,
  onCurrentProviderSlugChange,
  onCurrentHardwareIdChange,
  onCurrentGpuCountChange,
  onCurrentContractDaysLeftChange,
  onCurrentJobTypesChange,
  onTermsAcceptedChange,
  onCommit,
}: {
  selectedPool: ProviderPool;
  selectedHardware: HardwareOptionWithCostBand & { providerSlug?: string; providerName?: string };
  pools: ProviderPool[];
  hardwareOptions: Array<
    HardwareOptionWithCostBand & { providerSlug: string; providerName: string }
  >;
  selectedSlug: string;
  hardwareId: string;
  gpuCount: number;
  durationDays: number;
  intendedTasks: string[];
  currentProviderSlug: string;
  currentHardwareId: string;
  currentGpuCount: number;
  currentContractDaysLeft: number;
  currentJobTypes: string[];
  termsAccepted: boolean;
  onClose: () => void;
  onSelectedSlugChange: (slug: string) => void;
  onHardwareIdChange: (hardwareId: string) => void;
  onGpuCountChange: (value: number) => void;
  onDurationDaysChange: (value: number) => void;
  onIntendedTasksChange: (tasks: string[]) => void;
  onCurrentProviderSlugChange: (slug: string) => void;
  onCurrentHardwareIdChange: (hardwareId: string) => void;
  onCurrentGpuCountChange: (value: number) => void;
  onCurrentContractDaysLeftChange: (value: number) => void;
  onCurrentJobTypesChange: (tasks: string[]) => void;
  onTermsAcceptedChange: (checked: boolean) => void;
  onCommit: () => void;
}) {
  const modalProviderName = selectedSlug === "open_pool" ? "Open pool" : selectedPool.name;
  const toggleTask = (tasks: string[], task: string) =>
    tasks.includes(task) ? tasks.filter((item) => item !== task) : [...tasks, task];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/55 p-4 backdrop-blur-sm">
      <section className="max-h-[92vh] w-full max-w-3xl overflow-y-auto border border-[var(--wallet-ops-border-strong)] bg-[var(--wallet-ops-surface)] shadow-2xl">
        <div className="flex items-start justify-between gap-4 border-b border-[var(--wallet-ops-border)] p-5">
          <div className="flex items-start gap-3">
            <div className="wallet-ops-metric-icon">
              <FileText className="h-4 w-4" />
            </div>
            <div>
              <p className="wallet-ops-label">Demand commitment</p>
              <h2 className="mt-2 text-[24px] font-semibold tracking-normal text-[var(--wallet-ops-fg)]">
                {modalProviderName}
              </h2>
              <p className="mt-1 text-[13px] text-[var(--wallet-ops-subtle)]">
                Review the demand amount and acknowledge the commitment terms before submitting.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="wallet-ops-icon-button shrink-0"
            aria-label="Close commitment modal"
            title="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="grid gap-5 p-5">
          <section className="border border-[var(--wallet-ops-border)] bg-[var(--wallet-ops-surface-2)] p-4">
            <p className="wallet-ops-label">Current · if applicable</p>
            <div className="mt-4 grid gap-4 md:grid-cols-2">
              <label>
                <span className="mb-1 block text-[11px] font-semibold uppercase tracking-[0.16em] text-[var(--wallet-ops-muted)]">
                  Provider
                </span>
                <select
                  value={currentProviderSlug}
                  onChange={(event) => onCurrentProviderSlugChange(event.target.value)}
                  className="h-11 w-full border border-[var(--wallet-ops-border)] bg-[var(--wallet-ops-bg)] px-3 text-sm text-[var(--wallet-ops-fg)] outline-none focus:border-[var(--wallet-ops-border-strong)]"
                >
                  <option value="">Not applicable</option>
                  {pools.map((pool) => (
                    <option key={pool.slug} value={pool.slug}>
                      {pool.name}
                    </option>
                  ))}
                </select>
              </label>

              <label>
                <span className="mb-1 block text-[11px] font-semibold uppercase tracking-[0.16em] text-[var(--wallet-ops-muted)]">
                  Hardware
                </span>
                <select
                  value={currentHardwareId}
                  onChange={(event) => onCurrentHardwareIdChange(event.target.value)}
                  className="h-11 w-full border border-[var(--wallet-ops-border)] bg-[var(--wallet-ops-bg)] px-3 text-sm text-[var(--wallet-ops-fg)] outline-none focus:border-[var(--wallet-ops-border-strong)]"
                >
                  <option value="">Not applicable</option>
                  {hardwareOptions.map((hardware) => (
                    <option key={hardware.id} value={hardware.id}>
                      {pooledHardwareOptionLabel(hardware)}
                    </option>
                  ))}
                </select>
              </label>

              <NumberField
                label="Amount of GPU"
                value={currentGpuCount}
                min={0}
                step={1}
                onChange={onCurrentGpuCountChange}
              />
              <NumberField
                label="Time left on contract"
                value={currentContractDaysLeft}
                min={0}
                step={1}
                suffix="days"
                onChange={onCurrentContractDaysLeftChange}
              />
            </div>
            <TaskPicker
              className="mt-4"
              label="Type of jobs"
              selected={currentJobTypes}
              onChange={(task) => onCurrentJobTypesChange(toggleTask(currentJobTypes, task))}
              optional
            />
          </section>

          <section className="border border-[var(--wallet-ops-border)] bg-[var(--wallet-ops-surface)] p-4">
            <p className="wallet-ops-label">Requirements</p>
            <div className="mt-4 grid gap-4 md:grid-cols-2">
              <NumberField
                label="Amount of GPU"
                value={gpuCount}
                min={1}
                step={1}
                onChange={onGpuCountChange}
              />
              <NumberField
                label="Duration of contract"
                value={durationDays}
                min={1}
                step={1}
                suffix="days"
                onChange={onDurationDaysChange}
              />

              <label className="md:col-span-2">
                <span className="mb-1 block text-[11px] font-semibold uppercase tracking-[0.16em] text-[var(--wallet-ops-muted)]">
                  Hardware
                </span>
                <select
                  value={hardwareId}
                  onChange={(event) => onHardwareIdChange(event.target.value)}
                  className="h-11 w-full border border-[var(--wallet-ops-border)] bg-[var(--wallet-ops-bg)] px-3 text-sm text-[var(--wallet-ops-fg)] outline-none focus:border-[var(--wallet-ops-border-strong)]"
                >
                  {hardwareOptions.map((hardware) => (
                    <option key={hardware.id} value={hardware.id}>
                      {hardware.label} · {hardware.className} ·{" "}
                      {hardwareCostBandLabel(hardware.costBand)}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            <TaskPicker
              className="mt-4"
              label="Intended tasks"
              selected={intendedTasks}
              onChange={(task) => onIntendedTasksChange(toggleTask(intendedTasks, task))}
            />
          </section>

          <div className="grid gap-1 bg-[var(--wallet-ops-border)] md:grid-cols-3">
            <PoolStat label="Hardware" value={pooledHardwareOptionLabel(selectedHardware)} />
            <PoolStat
              label="Commitment"
              value={`${fmtGpuCount(gpuCount)} for ${durationDays} days`}
            />
            <PoolStat label="Derived" value={fmtGpuHours(gpuCount * durationDays * 24)} />
          </div>

          <CommitmentTerms
            checked={termsAccepted}
            onCheckedChange={onTermsAcceptedChange}
            providerName={
              selectedSlug === "open_pool" ? "the open compute pool" : selectedPool.name
            }
            hardwareLabel={pooledHardwareOptionLabel(selectedHardware)}
            gpuCount={gpuCount}
            durationDays={durationDays}
          />
        </div>

        <div className="flex flex-col gap-3 border-t border-[var(--wallet-ops-border)] p-5 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-[12px] text-[var(--wallet-ops-muted)]">
            This records demand intent now; final contract documents should be reviewed by counsel.
          </p>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onClose}
              className="wallet-ops-button min-h-[42px] justify-center px-4"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={onCommit}
              className="wallet-ops-button min-h-[42px] min-w-[178px] justify-center whitespace-nowrap px-4 font-semibold"
            >
              <Zap className="h-4 w-4" />
              Commit demand
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}

function CommitmentTerms({
  checked,
  onCheckedChange,
  providerName,
  hardwareLabel,
  gpuCount,
  durationDays,
}: {
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  providerName: string;
  hardwareLabel: string;
  gpuCount: number;
  durationDays: number;
}) {
  return (
    <div className="border border-[var(--warning)]/40 bg-[var(--warning)]/10 p-4">
      <div className="flex items-start gap-3">
        <input
          id="pooled-compute-terms"
          type="checkbox"
          checked={checked}
          onChange={(event) => onCheckedChange(event.target.checked)}
          className="mt-1 h-4 w-4 accent-[var(--success)]"
        />
        <label htmlFor="pooled-compute-terms" className="min-w-0 flex-1">
          <span className="block text-[12px] font-semibold uppercase tracking-[0.14em] text-[var(--wallet-ops-fg)]">
            Commitment terms
          </span>
          <a
            href="/terms-and-conditions"
            target="_blank"
            rel="noreferrer"
            className="mt-1 inline-flex text-[12px] font-medium text-primary underline-offset-4 hover:underline"
          >
            View Terms and Conditions
          </a>
          <span className="mt-2 block text-[13px] leading-6 text-[var(--wallet-ops-subtle)]">
            I confirm I am authorised to make this demand commitment for {providerName}. I
            understand that if a matching contract is offered for {fmtGpuCount(gpuCount)} of{" "}
            {hardwareLabel || "the selected hardware"} for {durationDays} days, I may be legally
            required to satisfy the committed amount and meet the resulting contract obligations.
          </span>
          <span className="mt-2 block text-[12px] leading-5 text-[var(--wallet-ops-muted)]">
            I also agree that submitted demand can be used to negotiate supply, pricing, and
            availability with providers.
          </span>
        </label>
      </div>
    </div>
  );
}

function PoolStat({ label, value }: { label: string; value: string }) {
  return (
    <div className="bg-[var(--wallet-ops-surface-2)] p-3">
      <p className="wallet-ops-label text-[10px]">{label}</p>
      <p className="mt-2 truncate text-[13px] font-medium text-[var(--wallet-ops-fg)]">{value}</p>
    </div>
  );
}

function DemandMetric({ label, value, hint }: { label: string; value: string; hint: string }) {
  return (
    <div className="min-h-[118px] bg-[var(--wallet-ops-surface)] p-5">
      <p className="wallet-ops-label">{label}</p>
      <p className="mt-3 truncate text-[28px] font-semibold leading-none text-[var(--wallet-ops-fg)]">
        {value}
      </p>
      <p className="mt-3 text-[12px] text-[var(--wallet-ops-subtle)]">{hint}</p>
    </div>
  );
}

function TaskPicker({
  label,
  selected,
  onChange,
  optional,
  className,
}: {
  label: string;
  selected: string[];
  onChange: (task: string) => void;
  optional?: boolean;
  className?: string;
}) {
  return (
    <div className={className}>
      <div className="mb-2 flex items-center justify-between gap-3">
        <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-[var(--wallet-ops-muted)]">
          {label}
        </p>
        {optional && (
          <span className="text-[10px] uppercase tracking-[0.12em] text-[var(--wallet-ops-muted)]">
            Optional
          </span>
        )}
      </div>
      <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
        {TASK_OPTIONS.map((task) => {
          const active = selected.includes(task.id);
          return (
            <button
              key={task.id}
              type="button"
              onClick={() => onChange(task.id)}
              className={cn(
                "min-h-[38px] border px-3 text-left text-[12px] font-medium transition",
                active
                  ? "border-[var(--wallet-ops-fg-strong)] bg-[var(--wallet-ops-fg-strong)] text-[var(--wallet-ops-bg)]"
                  : "border-[var(--wallet-ops-border)] bg-[var(--wallet-ops-bg)] text-[var(--wallet-ops-muted)] hover:border-[var(--wallet-ops-border-strong)] hover:text-[var(--wallet-ops-fg)]",
              )}
            >
              {task.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function NumberField({
  label,
  value,
  min,
  step,
  prefix,
  suffix,
  onChange,
}: {
  label: string;
  value: number;
  min: number;
  step: number;
  prefix?: string;
  suffix?: string;
  onChange: (value: number) => void;
}) {
  return (
    <label>
      <span className="mb-1 block text-[11px] font-semibold uppercase tracking-[0.16em] text-[var(--wallet-ops-muted)]">
        {label}
      </span>
      <div className="relative">
        {prefix && (
          <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-[var(--wallet-ops-muted)]">
            {prefix}
          </span>
        )}
        <input
          type="number"
          min={min}
          step={step}
          value={value}
          onChange={(event) => onChange(Number(event.target.value || 0))}
          className={cn(
            "h-11 w-full border border-[var(--wallet-ops-border)] bg-[var(--wallet-ops-bg)] px-3 text-sm text-[var(--wallet-ops-fg)] outline-none focus:border-[var(--wallet-ops-border-strong)]",
            prefix && "pl-7",
            suffix && "pr-14",
          )}
        />
        {suffix && (
          <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-sm text-[var(--wallet-ops-muted)]">
            {suffix}
          </span>
        )}
      </div>
    </label>
  );
}
